import type { RecordDto } from '@halo/shared';
import { dateOrdinal, formatDayMonth, parseDayMonth, type CalendarDate } from '../dates';
import {
  applyChanges,
  compactData,
  readNumber,
  readOptionalString,
  readString,
  type RecordData,
} from './parsing';
import { LEGACY_DEFAULT_YEAR } from './video';

export type WorklogStatus = 'todo' | 'done';

export interface WorklogEntry {
  recordId: number;
  owner: string | null;
  /**
   * Ngày của việc. Bản ghi cũ chỉ lưu «DD/MM» KHÔNG có năm (cùng lỗi mà video từng mắc): việc của
   * tháng 12 năm trước xem vào tháng 1 thì không phải «hôm nay» cũng chẳng phải «ngày cũ» nên
   * biến mất khỏi danh sách. Bản ghi mới lưu thêm `y`; bản ghi cũ coi là năm 2026.
   */
  date: CalendarDate | null;
  /** «HH:MM» — việc gõ tay: giờ ghi; việc do lịch hẹn tạo: giờ nhắc. */
  time: string;
  text: string;
  status: WorklogStatus;
  /** Mã lịch hẹn (`sched`) đã sinh ra việc này; `null` = gõ tay. Chỉ việc có nguồn mới đếm ngược. */
  scheduleCode: string | null;
}

export function parseWorklogEntry(record: RecordDto): WorklogEntry {
  const data = record.data;
  const dayMonth = parseDayMonth(readString(data, 'd'));
  return {
    recordId: record.id,
    owner: record.owner,
    date: dayMonth ? { ...dayMonth, year: readNumber(data, 'y') || LEGACY_DEFAULT_YEAR } : null,
    time: readString(data, 'at'),
    text: readString(data, 'txt'),
    status: data.st === 'done' ? 'done' : 'todo',
    scheduleCode: readOptionalString(data, 'src') ?? null,
  };
}

/** Việc GÕ TAY mới: chưa xong, không có lịch nguồn, giờ = lúc ghi. */
export function newWorklogEntry(text: string, now: Moment): WorklogEntry {
  return {
    recordId: 0,
    owner: null,
    date: now.date,
    time: formatClock(now.minutes),
    text,
    status: 'todo',
    scheduleCode: null,
  };
}

function toWorklogData(entry: WorklogEntry): RecordData {
  return {
    d: entry.date ? formatDayMonth(entry.date) : '',
    y: entry.date?.year,
    at: entry.time,
    txt: entry.text,
    st: entry.status,
    src: entry.scheduleCode,
  };
}

/** Có `stored` (đang SỬA): chỉ ghi đè khoá thật sự đổi — xem `applyChanges`. Không có: tạo mới. */
export function serializeWorklogEntry(entry: WorklogEntry, stored?: RecordDto): RecordData {
  const next = toWorklogData(entry);
  if (!stored) return compactData(next);
  return compactData(applyChanges(stored.data, toWorklogData(parseWorklogEntry(stored)), next));
}

/** «HH:MM» → số phút từ 0 giờ. Sai dạng → null. */
export function parseClock(text: string): number | null {
  const match = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(text);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
}

export function formatClock(minutes: number): string {
  const safe = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}

/** «2 giờ 5′» / «45′» — luôn là độ dài dương; «còn» hay «quá giờ» do nơi gọi quyết định. */
export function formatMinutesLeft(minutes: number): string {
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  return hours ? `${hours} giờ ${total % 60}′` : `${total}′`;
}

export interface Moment {
  date: CalendarDate;
  /** Số phút từ 0 giờ của `date`. */
  minutes: number;
}

export function momentOf(now: Date = new Date()): Moment {
  return {
    date: { day: now.getDate(), month: now.getMonth() + 1, year: now.getFullYear() },
    minutes: now.getHours() * 60 + now.getMinutes(),
  };
}

const utcDay = ({ day, month, year }: CalendarDate) => Date.UTC(year, month - 1, day) / 86_400_000;

/**
 * Số phút còn lại tới giờ nhắc (âm = đã quá giờ). `null` khi việc không đếm ngược: việc gõ tay,
 * việc đã xong, hoặc thiếu ngày/giờ.
 */
export function minutesLeft(entry: WorklogEntry, now: Moment): number | null {
  if (!entry.scheduleCode || entry.status !== 'todo' || !entry.date) return null;
  const due = parseClock(entry.time);
  if (due === null) return null;
  return (utcDay(entry.date) - utcDay(now.date)) * 1440 + due - now.minutes;
}

export function isOnDate(entry: WorklogEntry, date: CalendarDate): boolean {
  return entry.date !== null && dateOrdinal(entry.date) === dateOrdinal(date);
}

/** Trong một ngày: việc chưa xong trước, rồi theo giờ tăng dần. */
export function compareWithinDay(a: WorklogEntry, b: WorklogEntry): number {
  if (a.status !== b.status) return a.status === 'todo' ? -1 : 1;
  return (parseClock(a.time) ?? 0) - (parseClock(b.time) ?? 0) || a.recordId - b.recordId;
}

// ------------------------------------------------------------------ đếm ngược

export type CountdownTone = 'done' | 'late' | 'soon' | 'normal' | 'none';

export interface Countdown {
  countdown: string;
  tone: CountdownTone;
}

const SOON_MINUTES = 60;

/**
 * Lời đếm ngược của một việc («còn 45′», «quá giờ 2 giờ 5′», «xong») cho dòng nhật ký. Từng nằm ở
 * `domain/dashboard.ts` cho thẻ «Nhật ký hôm nay» của màn đầu (bỏ 03/10/2026 khi màn đầu chỉ còn Bảng tin).
 */
export function describeCountdown(entry: WorklogEntry, now: Moment): Countdown {
  if (entry.status === 'done') return { countdown: 'xong', tone: 'done' };
  const left = minutesLeft(entry, now);
  if (left === null) return { countdown: '', tone: 'none' };
  if (left < 0) return { countdown: `quá giờ ${formatMinutesLeft(left)}`, tone: 'late' };
  return {
    countdown: `còn ${formatMinutesLeft(left)}`,
    tone: left <= SOON_MINUTES ? 'soon' : 'normal',
  };
}
